

























































































import Vue, { VueConstructor } from "vue";
import storeHelper from "./store/store-helper";
import { AppMessage } from "./store/modules/site";
import ZenyaDialog, { IZenyaDialog } from "./components/dialogs/ZenyaDialog.vue";
import Loader from "./components/generic/Loader.vue";
import Utils, { PerformanceMeasurement } from "./utils";
import { Controllers, Portal, settingName, User } from "@infoland/iprova-api";
import { Constants } from "./constants";
import { PortalSettings } from "@/types/portal";
import { issue_type, License } from "@infoland/iprova-api/lib/types/license";
import moment from "moment";
import { TranslateResult } from "vue-i18n";
import { RouteMeta } from "./router";
import { MaintenanceModeSetting } from "@/types/settings";

//export the set of provided
export const AppInjects = ["$startCampaign"];
export interface IAppInjected {
    /**
     * Start a campaign
     * @param campaignId the campaign identifier
     */
    $startCampaign(campaignId: string): void;
}
export const AppComponent = Vue as VueConstructor<Vue & IAppInjected>;

export default Vue.extend({
    name: "App",
    components: { Loader, ZenyaDialog },
    data: () => ({
        confirmDialog: false,
        maintenanceMode: false,
        maintenanceHeight: 0,
        maintenanceTimer: null,
        licenseHasWarning: false,
        licenseWarningText: "",
        hasPanels: false,
        campaignId: null as string,
        overridePortalColor: null as string,
        intialDataLoaded: false
    }),
    provide: function () {
        return {
            $startCampaign: (campaignId: string) => {
                this.campaignId = campaignId;
            }
        };
    },
    mounted() {
        this.setPrimaryColor();
        //We've commented out this check for now. After the demo feedback, we decide whether or not to use it.
        //this.setSpecialColors();

        this.$nextTick(() => {
            //Set main bottom offset based on footer height
            this.setMainBottomOffSet();
        });
    },
    beforeDestroy() {
        //Clear maintenance
        clearInterval(this.maintenanceTimer);
    },
    computed: {
        message(): AppMessage {
            const message = storeHelper.siteStore.snackBarMessage;
            if (message.languageLookup) {
                return {
                    ...message,
                    text: this.$t(message.text, message.lookupData).toString()
                };
            } else {
                return message;
            }
        },
        primaryColor(): string {
            if (this.overridePortalColor) {
                //apply the override portal color;
                return this.overridePortalColor;
            } else {
                return this.portalSettings?.color ?? "#E41B51";
            }
        },
        portalSettings(): PortalSettings {
            if (this.portal) return this.portal;
            else return storeHelper.portalStore.defaultPortal;
        },
        portal(): Portal {
            if (this.portalId && Array.isArray(storeHelper.portalStore.portals)) {
                let portal = storeHelper.portalStore.getPortal(this.portalId);

                // preview portal has its own color
                if (window.location.href.indexOf("?") > -1) {
                    const colorExp: RegExp = /\&PortalColor=(......)/;
                    const portalColorResult: RegExpExecArray = colorExp.exec(window.location.href);

                    if (portalColorResult) {
                        portal.color = "#" + portalColorResult[1];
                        portal = { ...portal }; // make a shallow copy
                    }
                }

                return portal;
            } else {
                return null; // no portal or not loaded yet
            }
        },
        portalId(): number {
            if (this.$route.params.portalId) return parseInt(this.$route.params.portalId);
            else return null;
        },
        isInternetExplorer(): boolean {
            return Utils.isIE11;
        },
        appClass(): { [key: string]: boolean } {
            return {
                PageWithPanels: this.hasPanels
            };
        }
    },
    async created() {
        this.$router.afterEach(() => {
            this.hasPanels = false;
            this.campaignId = (this.$route.meta as RouteMeta).campaignId;
        });

        let performanceMeasurement = PerformanceMeasurement.Start();

        await this.loadRequiredData();

        performanceMeasurement.Stop("Load Duration - Init");

        //set licenses warning
        this.setLicenseWarningAsync();
        //set maintenance mode
        await this.setMaintenanceModeAsync();
    },
    methods: {
        async confirm(title: TranslateResult, message: TranslateResult): Promise<boolean> {
            return (this.$refs.confirmDialog as Vue & IZenyaDialog).open(title, message);
        },
        loadRequiredData: async function () {
            const userPromise = storeHelper.userStore.getLoggedOnUser();

            //load portals
            const portalsPomise = storeHelper.portalStore.loadPortals();

            //wait promises simultanious
            const [user] = await Promise.all([userPromise, portalsPomise]);

            //start loading teams (only when not anonymnous)
            if (!Utils.isAnonymousUser(user.user_id)) storeHelper.teamsStore.loadTeamsAsync();

            //get override color setting. (don't need to wait for this)
            storeHelper.settingsStore.getSettingAsync<string>("override_portal_color")?.then(override => {
                this.overridePortalColor = override;
            });

            this.intialDataLoaded = true;
        },

        setPrimaryColor: function () {
            this.$vuetify.theme.themes.dark.primary = this.primaryColor;
            this.$vuetify.theme.themes.light.primary = this.primaryColor;
        },
        setSpecialColors: function () {
            // default Vuetify accent colors
            const errorColor = "#FF5252";
            const infoColor = "#2196F3";
            const warningColor = "#FB8C00";
            const successColor = "#4CAF50";

            // read the colors and transform them into rgb format
            const primaryColorRGB = this.toRgb(this.$vuetify.theme.themes.light.primary);
            const errorColorRGB = this.toRgb(errorColor);
            const infoColorRGB = this.toRgb(infoColor);
            const warningColorRGB = this.toRgb(warningColor);
            const successColorRGB = this.toRgb(successColor);

            // calculate the relative luminance
            const primaryColorLuminance = this.luminance(
                parseInt(primaryColorRGB[1], 16),
                parseInt(primaryColorRGB[2], 16),
                parseInt(primaryColorRGB[3], 16)
            );
            const errorColorLuminance = this.luminance(
                parseInt(errorColorRGB[1], 16),
                parseInt(errorColorRGB[2], 16),
                parseInt(errorColorRGB[3], 16)
            );
            const infoColorLuminance = this.luminance(parseInt(infoColorRGB[1], 16), parseInt(infoColorRGB[2], 16), parseInt(infoColorRGB[3], 16));
            const warningColorLuminance = this.luminance(
                parseInt(warningColorRGB[1], 16),
                parseInt(warningColorRGB[2], 16),
                parseInt(warningColorRGB[3], 16)
            );
            const successColorLuminance = this.luminance(
                parseInt(successColorRGB[1], 16),
                parseInt(successColorRGB[2], 16),
                parseInt(successColorRGB[3], 16)
            );

            // calculate the color contrast ratio
            const errorRatio = this.calculateColorRatio(primaryColorLuminance, errorColorLuminance);
            const infoRatio = this.calculateColorRatio(primaryColorLuminance, infoColorLuminance);
            const warningRatio = this.calculateColorRatio(primaryColorLuminance, warningColorLuminance);
            const successRatio = this.calculateColorRatio(primaryColorLuminance, successColorLuminance);

            //Check the color contrast ratio
            if (errorRatio > 1 / 1.5) {
                this.$vuetify.theme.themes.dark.error = this.$vuetify.theme.themes.light.primary;
                this.$vuetify.theme.themes.light.error = this.$vuetify.theme.themes.light.primary;
            } else {
                this.$vuetify.theme.themes.dark.error = errorColor; // default vuetify error color
                this.$vuetify.theme.themes.light.error = errorColor; // default vuetify error color
            }

            if (infoRatio > 1 / 1.5) {
                this.$vuetify.theme.themes.dark.info = this.$vuetify.theme.themes.light.primary;
                this.$vuetify.theme.themes.light.info = this.$vuetify.theme.themes.light.primary;
            } else {
                this.$vuetify.theme.themes.dark.info = infoColor; // default vuetify info color
                this.$vuetify.theme.themes.light.info = infoColor; // default vuetify info color
            }

            if (warningRatio > 1 / 1.5) {
                this.$vuetify.theme.themes.dark.warning = this.$vuetify.theme.themes.light.primary;
                this.$vuetify.theme.themes.light.warning = this.$vuetify.theme.themes.light.primary;
            } else {
                this.$vuetify.theme.themes.dark.warning = warningColor; // default vuetify warning color
                this.$vuetify.theme.themes.light.warning = warningColor; // default vuetify warning color
            }

            if (successRatio > 1 / 1.5) {
                this.$vuetify.theme.themes.dark.success = this.$vuetify.theme.themes.light.primary;
                this.$vuetify.theme.themes.light.success = this.$vuetify.theme.themes.light.primary;
            } else {
                this.$vuetify.theme.themes.dark.success = successColor; // default vuetify success color
                this.$vuetify.theme.themes.light.success = successColor; // default vuetify success color
            }
        },
        toRgb: function (hex): RegExpExecArray {
            var shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
            hex = hex.replace(shorthandRegex);
            return /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        },
        luminance: function (r, g, b): Number {
            var a = [r, g, b].map(function (v) {
                v /= 255;
                return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
            });
            return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
        },
        setHasPanels(val: boolean) {
            this.hasPanels = val;
        },
        calculateColorRatio: function (primaryColorLuminance, specialColorLuminance): Number {
            return primaryColorLuminance > specialColorLuminance
                ? (specialColorLuminance + 0.05) / (primaryColorLuminance + 0.05)
                : (primaryColorLuminance + 0.05) / (specialColorLuminance + 0.05);
        },
        async setMaintenanceModeAsync() {
            const maintenanceModeConfig = await storeHelper.settingsStore.getSettingAsync<MaintenanceModeSetting>(settingName.maintenance_mode);

            this.maintenanceMode = maintenanceModeConfig.enabled;
            const interval = maintenanceModeConfig.check_interval_seconds ? maintenanceModeConfig.check_interval_seconds : 60;

            //set maintenance timer checker
            if (this.maintenanceTimer) {
                clearInterval(this.maintenanceTimer);
            }
            this.maintenanceTimer = window.setInterval(await this.setMaintenanceModeAsync, interval * 1000);
        },
        async setLicenseWarningAsync() {
            let license = (await Controllers.LicenseController.getLicenseAsync()) as License;

            if (license && license.has_issue && license.issue_type !== issue_type.none) {
                let nowDateLicenseExceededISO: Date = new Date();
                nowDateLicenseExceededISO.setDate(nowDateLicenseExceededISO.getDate() - Constants.LicensedParts.ExceededWarningDays);

                let nowDateNoConnectionWithLicenseServerISO: Date = new Date();
                nowDateNoConnectionWithLicenseServerISO.setDate(
                    nowDateNoConnectionWithLicenseServerISO.getDate() - Constants.LicensedParts.NoConnectionWithLicenseServerWarningDays
                );

                let issueStartedDate: Date = new Date(moment(license.issue_started_datetime, "YYYY-MM-DD HH:mm").toString());
                if (license.issue_type === issue_type.licensed_part_exceeded && nowDateLicenseExceededISO >= issueStartedDate) {
                    this.licenseHasWarning = true;
                    this.licenseWarningText = this.$tc("general.messages.licensedPartExceededWarning");
                } else if (
                    license.issue_type === issue_type.no_connection_with_license_server &&
                    nowDateNoConnectionWithLicenseServerISO >= issueStartedDate
                ) {
                    this.licenseHasWarning = true;
                    this.licenseWarningText = this.$tc("general.messages.noConnectionWithLicenseServer");
                }
            }
        },
        setMainBottomOffSet: function () {
            if (this.licenseHasWarning || this.maintenanceMode) {
                const mainPage = document.getElementById("main");
                const footerHeight = (this.$refs.footer as Vue)?.$el.clientHeight;

                mainPage.style.paddingBottom = footerHeight.toString() + "px";
            }
        }
    },
    watch: {
        primaryColor() {
            this.setPrimaryColor();
            //We've commented out this check for now. After the demo feedback, we decide whether or not to use it.
            //this.setSpecialColors();
        }
    }
});
